/**
 * Fixed on purpose. This is a yardstick, not a real job — changing it changes
 * what every past and future impact comparison means. Do not tune it to make a
 * delta look better.
 *
 * The payload is deliberately database-independent: a single custom-mode line
 * whose components carry their own costs. `calculate_job_costs` routes
 * `mode: "custom"` through `calculate_custom_assembly_costs`, which reads only
 * the request body — no Product, ProductType, Material, LaborRateCard or
 * FinishRate row is consulted. So the comparison means the same thing on every
 * machine, whatever happens to be seeded locally.
 */

export const REFERENCE_QUOTE = {
  job_name: "Reference quote (pricing check)",
  line_items: [
    {
      mode: "custom",
      name: "Reference panel",
      quantity: 4,
      assembly_components: [
        {
          type: "material",
          name: "PVC sheet",
          unit: "each",
          quantity_per_unit: 1,
          unit_cost: 120,
        },
        {
          type: "operation",
          name: "Fabrication",
          department: "fab",
          hours_per_unit: 2,
        },
        {
          type: "operation",
          name: "Finish",
          department: "finish",
          hours_per_unit: 1,
        },
        {
          type: "hardware",
          name: "Fasteners",
          quantity_per_unit: 1,
          unit_cost: 15,
        },
      ],
    },
  ],
  material_origination_cost: 0,
  material_financing_percent: 0,
  subcontractor_install_cost: 0,
  install_hours: 8,
  install_labor_rate: 50,
  delivery_override: 200,
  equipment_rental_cost: 0,
  hardware_costs: 0,
  powder_coating_costs: 0,
  fasteners_screws_costs: 0,
};

/**
 * The nine rate fields the calculator actually reads: four in
 * `_resolve_job_rates` (which labor rate, and the three rate values) and five
 * in `_apply_final_pricing_summary`.
 *
 * `labor_rate_type` is the PricingSettings field name; the calculator reads the
 * selector as `laborRateType` first and `labor_rate_type` second. Both are sent
 * so a rename on either side can never silently compare the wrong labor rate.
 *
 * The selector itself was retired 2026-08-18 — the shop labor (burden) rate is
 * the engine's labor input — so `labor_rate_type` no longer moves. It stays in
 * this list because the settings row still carries it and a legacy value must
 * be reflected in the comparison rather than quietly replaced.
 */
export const REFERENCE_RATE_FIELDS = [
  "labor_rate_type",
  "labor_burden_rate",
  "island_millwork_labor_rate",
  "labor_custom_rate",
  "overhead_ratio",
  "profit_margin",
  "material_sales_tax_rate",
  "commission_rate",
  "shop_commission_profit_sharing_rate",
];

/** The five constants an admin edits per client tier, in reading order. */
export const REFERENCE_PERCENT_FIELDS = [
  "overhead_ratio",
  "profit_margin",
  "material_sales_tax_rate",
  "commission_rate",
  "shop_commission_profit_sharing_rate",
];

/** Pull just the nine rate fields out of a settings object. */
export function extractRateConstants(settings = {}) {
  return REFERENCE_RATE_FIELDS.reduce((accumulator, field) => {
    accumulator[field] = settings?.[field];
    return accumulator;
  }, {});
}

/** True when any of the nine constants differ between two settings objects. */
export function rateConstantsDiffer(before = {}, after = {}) {
  return REFERENCE_RATE_FIELDS.some((field) => {
    const a = before?.[field];
    const b = after?.[field];
    if (field === "labor_rate_type") return (a || "") !== (b || "");
    const numA = Number.parseFloat(a);
    const numB = Number.parseFloat(b);
    if (Number.isNaN(numA) && Number.isNaN(numB)) return false;
    return numA !== numB;
  });
}

/**
 * Merge the frozen reference job with a set of rate constants. Everything about
 * the job itself stays fixed; only the rates move.
 */
export function buildReferencePayload(constants = {}) {
  return {
    ...REFERENCE_QUOTE,
    // The calculator prefers `laborRateType` (camelCase); PricingSettings state
    // spells it `labor_rate_type`. Send both — a miss here silently compares
    // the wrong labor rate. Absent means burden (the shop labor rate), never
    // the retired $30 custom fallback.
    laborRateType: constants.labor_rate_type || "burden",
    labor_rate_type: constants.labor_rate_type || "burden",
    labor_burden_rate: constants.labor_burden_rate,
    island_millwork_labor_rate: constants.island_millwork_labor_rate,
    labor_custom_rate: constants.labor_custom_rate,
    overhead_ratio: constants.overhead_ratio,
    profit_margin: constants.profit_margin,
    material_sales_tax_rate: constants.material_sales_tax_rate,
    commission_rate: constants.commission_rate,
    shop_commission_profit_sharing_rate:
      constants.shop_commission_profit_sharing_rate,
  };
}

export default REFERENCE_QUOTE;
